import React, { useState, useEffect, useMemo, useRef } from 'react';
import { renderMarkdown } from '../../utils/markdown';
import {
  MessageActionMenu,
  MessageReactionStrip,
  PinnedMessagesStrip,
  ReplyComposerPreview,
  ReplyContext,
  messagePreview
} from './MessageActions';
import Icons from '../common/Icons';

const CHAT_EMOJIS = [
  '\u{1F600}', '\u{1F604}', '\u{1F602}', '\u{1F60A}', '\u{1F60D}', '\u{1F60E}',
  '\u{1F91D}', '\u{1F64F}', '\u{1F44D}', '\u{1F525}', '\u{2728}', '\u{1F680}',
  '\u{1F512}', '\u{1F6E1}\u{FE0F}', '\u{26A1}', '\u{2705}', '\u{2757}', '\u{2764}\u{FE0F}'
];

export const ChatPane = ({
  activeChatContact,
  chatMessages,
  activeIdentity,
  chatInputText,
  setChatInputText,
  handleSendChatMessage,
  setActiveChatContact,
  showEmojiPicker,
  setShowEmojiPicker,
  handleDeleteChatMessage,
  handleClearDirectChat,
  openContactProfile,
  messageMeta = {},
  onToggleMessagePin,
  onToggleMessageSaved,
  onReactToMessage,
  messageReplyTarget,
  setMessageReplyTarget,
  t,
  displayGaiaID,
  getSenderRoles
}) => {
  const scrollRef = useRef(null);
  const longPressTimerRef = useRef(null);
  const [actionMessageId, setActionMessageId] = useState(null);
  const [isPeerTyping, setIsPeerTyping] = useState(false);

  // Message Edits State (Zero-knowledge local persistence)
  const [chatEdits, setChatEdits] = useState(() => {
    if (!activeIdentity?.ID) return {};
    try {
      const stored = localStorage.getItem(`gaia_chat_edits_${activeIdentity.ID}`);
      return stored ? JSON.parse(stored) : {};
    } catch (_) {
      return {};
    }
  });

  const [editingMessageId, setEditingMessageId] = useState(null);
  const [editInputText, setEditInputText] = useState('');

  // Blocked contacts state
  const [isBlocked, setIsBlocked] = useState(false);

  useEffect(() => {
    if (activeChatContact && activeIdentity) {
      const stored = localStorage.getItem(`contacts_${activeIdentity.UserID || activeIdentity.userId || 'default'}`);
      if (stored) {
        try {
          const list = JSON.parse(stored);
          const match = list.find(c => c.gaiaID === activeChatContact.gaiaID);
          setIsBlocked(!!match?.blocked);
        } catch (_) {}
      }
    }
  }, [activeChatContact, activeIdentity]);

  const handleBlockContact = () => {
    if (!activeChatContact || !activeIdentity) return;
    const nextBlocked = !isBlocked;
    setIsBlocked(nextBlocked);

    const storedKey = `contacts_${activeIdentity.UserID || activeIdentity.userId || 'default'}`;
    const stored = localStorage.getItem(storedKey);
    if (stored) {
      try {
        const list = JSON.parse(stored);
        const updated = list.map(c => c.gaiaID === activeChatContact.gaiaID ? { ...c, blocked: nextBlocked } : c);
        localStorage.setItem(storedKey, JSON.stringify(updated));
      } catch (_) {}
    }
  };

  const handleReportAbuse = () => {
    if (!activeChatContact || !activeIdentity) return;
    // Show a confirmation or call a prompt to get a comment
    const comment = prompt("Bitte geben Sie einen Grund für die Missbrauchsmeldung ein:");
    if (comment === null) return; // Cancelled
    
    // Call backend submitAbuseReport endpoint via fetch or import
    import('../../api').then(async (api) => {
      try {
        await api.submitAbuseReport(
          activeIdentity.ID,
          'user',
          activeChatContact.ID || activeChatContact.gaiaID,
          'harassment',
          'medium',
          null,
          comment
        );
        alert('Vielen Dank. Die Meldung wurde erfolgreich an die Governance-Moderatoren übermittelt.');
      } catch (err) {
        alert('Fehler beim Einreichen des Reports: ' + err.message);
      }
    });
  };

  const appendChatEmoji = emoji => {
    setChatInputText(prev => prev + emoji);
    setShowEmojiPicker(false);
  };

  const visibleMessages = useMemo(() => {
    if (!activeChatContact || !activeIdentity) return [];
    return chatMessages.filter(msg =>
      (msg.sender === activeChatContact.gaiaID && msg.recipient === activeIdentity.GaiaID) ||
      (msg.sender === activeIdentity.GaiaID && msg.recipient === activeChatContact.gaiaID)
    );
  }, [activeChatContact, activeIdentity, chatMessages]);

  const lastMessageId = visibleMessages.length > 0 ? visibleMessages[visibleMessages.length - 1].id : '';

  // Scroll to bottom on new messages
  useEffect(() => {
    if (!scrollRef.current) return;
    scrollRef.current.scrollTo({
      top: scrollRef.current.scrollHeight,
      behavior: 'smooth'
    });
  }, [activeChatContact?.ID, lastMessageId, visibleMessages.length]);

  // Simulated peer typing effect when user typing or sending message
  useEffect(() => {
    if (visibleMessages.length > 0) {
      const lastMsg = visibleMessages[visibleMessages.length - 1];
      const isOutgoing = lastMsg.sender === activeIdentity.GaiaID;
      if (isOutgoing) {
        const timer = setTimeout(() => {
          setIsPeerTyping(true);
          const stopTimer = setTimeout(() => {
            setIsPeerTyping(false);
          }, 3500);
          return () => clearTimeout(stopTimer);
        }, 1500);
        return () => clearTimeout(timer);
      }
    }
  }, [visibleMessages.length, activeIdentity.GaiaID]);

  useEffect(() => () => {
    if (longPressTimerRef.current) {
      window.clearTimeout(longPressTimerRef.current);
    }
  }, []);

  const openMessageActions = (event, messageId) => {
    event.preventDefault();
    event.stopPropagation();
    setActionMessageId(messageId);
  };

  const startLongPress = messageId => {
    if (longPressTimerRef.current) {
      window.clearTimeout(longPressTimerRef.current);
    }
    longPressTimerRef.current = window.setTimeout(() => {
      setActionMessageId(messageId);
    }, 520);
  };

  const cancelLongPress = () => {
    if (longPressTimerRef.current) {
      window.clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  };

  const jumpToMessage = messageId => {
    if (!scrollRef.current) return;
    const target = scrollRef.current.querySelector(`[data-message-id="${messageId}"]`);
    if (target) {
      target.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  const replyToMessage = message => {
    setMessageReplyTarget({
      messageId: message.id,
      sender: message.sender,
      senderGaia: message.senderGaia,
      bodyPreview: messagePreview(message),
      createdAt: message.createdAt
    });
  };

  const startEditingMessage = message => {
    setEditingMessageId(message.id);
    setEditInputText(chatEdits[message.id] || message.body);
  };

  const saveEditedMessage = (messageId) => {
    if (!editInputText.trim()) return;
    const nextEdits = { ...chatEdits, [messageId]: editInputText };
    setChatEdits(nextEdits);
    if (activeIdentity?.ID) {
      localStorage.setItem(`gaia_chat_edits_${activeIdentity.ID}`, JSON.stringify(nextEdits));
    }
    setEditingMessageId(null);
    setEditInputText('');
  };

  if (!activeChatContact) {
    return (
      <div className="chat-container empty-chat-state">
        <h3>{t('kein_chat_ausgewaehlt') || 'Kein Chat ausgewählt'}</h3>
        <p>{t('select_contact_chat') || 'Wähle einen Kontakt aus dem linken Panel aus, um einen quantensicheren E2E-Chat zu starten.'}</p>
      </div>
    );
  }

  return (
    <div className="chat-container">
      <header className="reader-header chat-reader-header">
        <button type="button" className="mobile-back-btn" onClick={() => setActiveChatContact(null)}>
          {'<'} {t('quanten_chat') || 'Chats'}
        </button>
        <div className="chat-peer-header">
          <div className="chat-peer-icon" aria-hidden="true">{'\u{1F4AC}'}</div>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
              <button type="button" className="link-button contact-name-button" onClick={() => openContactProfile(activeChatContact.gaiaID)}>
                {activeChatContact.displayName}
              </button>
              {getSenderRoles && getSenderRoles(activeChatContact.gaiaID)?.map(role => {
                let bg = 'rgba(255,255,255,0.05)';
                let color = 'var(--text-secondary)';
                let border = '1px solid var(--border-color)';
                let label = role;

                if (role === 'node_operator') {
                  bg = 'rgba(168, 85, 247, 0.15)';
                  color = '#c084fc';
                  border = '1px solid rgba(168, 85, 247, 0.3)';
                  label = t('node_operator') || 'Node Operator';
                } else if (role === 'senior_reviewer') {
                  bg = 'rgba(249, 115, 22, 0.15)';
                  color = '#fdba74';
                  border = '1px solid rgba(249, 115, 22, 0.3)';
                  label = t('senior_reviewer') || 'Senior Reviewer';
                } else if (role === 'trusted_reviewer') {
                  bg = 'rgba(20, 184, 166, 0.15)';
                  color = '#2dd4bf';
                  border = '1px solid rgba(20, 184, 166, 0.3)';
                  label = t('trusted_reviewer') || 'Trusted Reviewer';
                } else {
                  label = t(role) || role;
                }

                return (
                  <span
                    key={role}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      padding: '1px 6px',
                      borderRadius: '10px',
                      fontSize: '0.65rem',
                      fontWeight: '600',
                      background: bg,
                      color: color,
                      border: border,
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em'
                    }}
                  >
                    {label}
                  </span>
                );
              })}
            </div>
            <span>{displayGaiaID(activeChatContact.gaiaID)}</span>
          </div>
        </div>

        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
          <button type="button" className={`btn-secondary ${isBlocked ? 'btn-danger' : ''}`} onClick={handleBlockContact}>
            {isBlocked ? 'Entblocken' : 'Blockieren'}
          </button>
          <button type="button" className="btn-secondary" onClick={handleReportAbuse}>
            Melden
          </button>
          <button type="button" className="btn-secondary chat-header-action" onClick={handleClearDirectChat}>
            {t('clear_chat') || 'Clear Chat'}
          </button>
        </div>
      </header>

      {isBlocked && (
        <div className="smtp-security-banner" style={{ background: 'rgba(255, 59, 48, 0.15)', borderColor: 'var(--danger)', padding: '12px', textAlign: 'center', color: '#ff4d4f' }}>
          <strong>⛔ Dieser Kontakt ist blockiert.</strong> Sie können keine Nachrichten senden oder empfangen.
        </div>
      )}

      <PinnedMessagesStrip
        messages={visibleMessages}
        messageMeta={messageMeta}
        onJumpToMessage={jumpToMessage}
        t={t}
      />

      <div className="chat-messages gaia-scrollbar" ref={scrollRef} onClick={() => setActionMessageId(null)}>
        {visibleMessages.map(msg => {
          const isOutgoing = msg.sender === activeIdentity.GaiaID;
          const meta = messageMeta[msg.id] || {};
          const isEditing = editingMessageId === msg.id;
          const hasBeenEdited = !!chatEdits[msg.id];
          const displayedBody = chatEdits[msg.id] || msg.body;

          return (
            <div
              key={msg.id}
              data-message-id={msg.id}
              className={`chat-bubble ${isOutgoing ? 'outgoing' : 'incoming'} ${meta.pinned ? 'pinned' : ''} ${meta.saved ? 'saved' : ''}`}
              onContextMenu={event => openMessageActions(event, msg.id)}
              onTouchStart={() => startLongPress(msg.id)}
              onTouchEnd={cancelLongPress}
              onTouchMove={cancelLongPress}
              style={{ paddingBottom: '16px' }}
            >
              <ReplyContext replyTo={msg.replyTo} t={t} />
              
              {isEditing ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '6px' }}>
                  <textarea
                    className="input-field"
                    value={editInputText}
                    onChange={e => setEditInputText(e.target.value)}
                    style={{ background: 'var(--bg-dark)', minHeight: '60px', resize: 'none' }}
                  />
                  <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                    <button type="button" className="btn-secondary" style={{ padding: '4px 10px', fontSize: '0.75rem' }} onClick={() => setEditingMessageId(null)}>Abbrechen</button>
                    <button type="button" className="btn-primary" style={{ padding: '4px 10px', fontSize: '0.75rem' }} onClick={() => saveEditedMessage(msg.id)}>Speichern</button>
                  </div>
                </div>
              ) : (
                <div style={{ position: 'relative' }}>
                  {renderMarkdown(displayedBody)}
                  {hasBeenEdited && (
                    <span style={{ fontSize: '0.65rem', color: 'var(--text-muted)', display: 'block', fontStyle: 'italic', marginTop: '4px' }}>
                      (bearbeitet)
                    </span>
                  )}
                </div>
              )}

              <MessageReactionStrip meta={meta} />
              
              <div className="chat-bubble-meta" style={{ display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'flex-end', marginTop: '4px' }}>
                <span>{new Date(msg.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                {meta.saved && <span style={{ color: 'var(--accent-cyan)' }}>{t('message_saved') || 'Saved'}</span>}
                
                {isOutgoing && !isEditing && (
                  <button
                    type="button"
                    className="chat-delete-btn"
                    style={{ background: 'transparent', border: 'none', color: 'var(--text-muted)', fontSize: '0.75rem', cursor: 'pointer', padding: 0 }}
                    onClick={() => startEditingMessage(msg)}
                  >
                    Bearbeiten
                  </button>
                )}

                <button type="button" className="chat-delete-btn" onClick={() => handleDeleteChatMessage(msg.id)}>
                  {t('delete') || 'Delete'}
                </button>
                {msg.untrusted && <span className="chat-untrusted">{t('untrusted') || 'Untrusted'}</span>}
                
                {/* Checkmarks */}
                {isOutgoing && (
                  <span style={{ fontSize: '0.85rem', color: msg.isRead ? 'var(--accent-cyan)' : 'var(--text-muted)', marginLeft: '4px', fontWeight: 'bold' }} title={msg.isRead ? 'Gelesen' : 'Zugestellt'}>
                    ✓✓
                  </span>
                )}
              </div>

              <MessageActionMenu
                open={actionMessageId === msg.id}
                message={msg}
                meta={meta}
                onClose={() => setActionMessageId(null)}
                onTogglePin={onToggleMessagePin}
                onToggleSave={onToggleMessageSaved}
                onReply={replyToMessage}
                onReact={onReactToMessage}
                t={t}
              />
            </div>
          );
        })}

        {isPeerTyping && (
          <div className="typing-indicator" style={{ display: 'flex', gap: '4px', padding: '8px 12px', background: 'rgba(0, 242, 254, 0.05)', borderRadius: '12px', width: 'fit-content', margin: '8px', border: '1px solid rgba(0, 242, 254, 0.15)' }}>
            <span style={{ fontSize: '0.75rem', color: 'var(--accent-cyan)', display: 'flex', alignItems: 'center', gap: '6px' }}>
              💬 {activeChatContact.displayName} schreibt...
            </span>
          </div>
        )}

        {visibleMessages.length === 0 && (
          <div className="chat-empty-hint">
            {t('chat_start_hint') || 'Starten Sie die Konversation! Alle Chat-Nachrichten sind quantensicher E2E verschlüsselt.'}
          </div>
        )}
      </div>

      <div className="chat-composer-stack">
        <ReplyComposerPreview
          replyTarget={messageReplyTarget}
          onClear={() => setMessageReplyTarget(null)}
          t={t}
        />
        <form className="chat-input-row" onSubmit={handleSendChatMessage}>
          <div className="emoji-control">
            <button type="button" className="btn-secondary emoji-toggle" onClick={() => setShowEmojiPicker(prev => !prev)} disabled={isBlocked}>
              {'\u{1F642}'}
            </button>
            {showEmojiPicker && (
              <div className="emoji-picker" role="listbox" aria-label="Emoji Auswahl">
                {CHAT_EMOJIS.map(emoji => (
                  <button type="button" key={emoji} onClick={() => appendChatEmoji(emoji)}>
                    {emoji}
                  </button>
                ))}
              </div>
            )}
          </div>
          <input
            type="text"
            className="input-field"
            placeholder={isBlocked ? "Chat blockiert" : (t('chat_input_placeholder') || 'Sichere Nachricht eingeben...')}
            value={chatInputText}
            onChange={event => setChatInputText(event.target.value)}
            disabled={isBlocked}
            required
          />
          <button type="submit" className="btn-primary" disabled={isBlocked}>
            {t('senden') || 'Senden'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default ChatPane;
